import React, { useState } from 'react';
import * as Yup from 'yup';
import { Button, Select2 } from '@economic/taco';
import { t, Trans } from '@lingui/macro';
import { useCurrentUser } from '@economic/react-app';
import { Field, Form } from '../../shared/components/Form';
import useMutation from '../../shared/hooks/useMutation';
import { cvrLookupInternal } from '../../shared/util/utilities/cvrLookup';
import { debouncePromise } from '../../shared/util/utilities/debouncePromise';
import { getCityNameFromPostCodeInternal } from '../../shared/util/utilities/getCityNameFromPostcode';
import { isValidCvr } from '../../shared/util/utilities/cvrUtils';
import { CompanyType, OnboardingStatusCompanyData, VismaUserProfileNameData } from './types';

const fetchCityFromZipcode = debouncePromise(getCityNameFromPostCodeInternal);

const fetchCompanyInfoByCvr = debouncePromise(cvrLookupInternal);

interface Props {
    onDone: () => void;
    setCompanyType: (value) => void;
    companyData: OnboardingStatusCompanyData;
    vismaUserProfileNameData: VismaUserProfileNameData;
    companyTypes: CompanyType[];
    companyType?: string[];
    children: string | JSX.Element;
}

const businessInfoValidationSchema = () =>
    Yup.object().shape({
        firstName: Yup.string().required(),
        lastName: Yup.string().required(),
        name: Yup.string().required(),
        email: Yup.string()
            .email(t`Please enter a valid email address.`)
            .required(),
        zipCode: Yup.string().required(),
        city: Yup.string().required(),
        phone: Yup.string()
            .matches(/^\+?[\d-. ()/]+$/, t`Please enter a valid phone number (e.g. +45 11 11 11 11).`)
            .required(),
        address: Yup.string().required(),
    });

export default function StepBusinessInfo({
    companyData,
    vismaUserProfileNameData,
    companyTypes,
    onDone,
    setCompanyType,
    companyType,
    children,
}: Props) {
    const updateCompanyData = useMutation('onboarding/company-data', 'PUT');
    const updateUserData = useMutation('onboarding/user-data', 'PUT');
    const [showCompanyTypeError, setShowCompanyTypeError] = useState<boolean>(false);
    const user = useCurrentUser();

    if (!user) {
        return null;
    }

    async function saveBusinessInfo(values) {
        if (!companyType || companyType.length === 0) {
            setShowCompanyTypeError(true);
            return;
        }

        const dataToSave = {
            name: values.name,
            cvr: values.cvr,
            address: values.address,
            zipCode: values.zipCode,
            city: values.city,
            contact: values.firstName + ' ' + values.lastName,
            email: values.email,
            phone: values.phone,
            website: values.website,
        };

        setShowCompanyTypeError(false);
        await updateCompanyData(dataToSave);

        if (vismaUserProfileNameData?.firstName === null && vismaUserProfileNameData?.lastName === null) {
            await updateUserData({
                firstName: values.firstName,
                lastName: values.lastName,
                email: values.email,
            });
        }

        onDone();
    }

    const updates = [
        {
            field: 'cvr',
            updates: async value => {
                if (user.agreement.edition === 1) {
                    if (!isValidCvr) {
                        return {};
                    }

                    try {
                        const data = await fetchCompanyInfoByCvr(value);
                        if (!!data && `${data.cvr}` === value) {
                            return {
                                name: data.name,
                                address: data.address.lines,
                                cvr: data.cvr,
                                phone: data.phone,
                                email: data.email,
                                zipCode: data.address.postcode,
                                city: data.address.city,
                                companyType,
                            };
                        }
                    } catch (e) {
                        // do nothing, this is autocomplete
                    }
                }
                return {};
            },
        },
        {
            field: 'zipCode',
            updates: async zipCode => {
                try {
                    const city = await fetchCityFromZipcode(zipCode);
                    if (city) {
                        return { city };
                    }
                } catch (e) {
                    // do nothing, this is autocomplete
                }

                return {};
            },
        },
    ];

    return (
        <div className="onboarding__container__fixed-size">
            <div className="onboarding onboarding__business-info">
                {children}

                <h2>
                    <Trans>Tell us about your business</Trans>
                </h2>

                <p>
                    <Trans>This information can be changed at any time.</Trans>
                </p>
                <Form
                    skipNotifications
                    onSubmit={saveBusinessInfo}
                    updates={updates}
                    initialData={{
                        name: companyData.name,
                        cvr: companyData.cvr,
                        address: companyData.address,
                        zipCode: companyData.zipCode,
                        city: companyData.city,
                        firstName: vismaUserProfileNameData?.firstName ?? companyData.contact?.replace(' Efternavn', ''),
                        lastName: vismaUserProfileNameData?.lastName ?? '',
                        email: companyData.email,
                        phone: companyData.phone,
                        website: companyData.website,
                    }}
                    validationSchema={businessInfoValidationSchema}>
                    {({ form }) => (
                        <div>
                            <div className="onboarding__form-content" style={{ maxWidth: '600px' }}>
                                <div className="columns">
                                    <Field.Text name="firstName" label={t`First name`} required maxLength={50} />
                                    <Field.Text name="lastName" label={t`Last name`} required maxLength={50} />
                                    <Field.Text name="cvr" label={t`CVR-no.`} maxLength={8} />
                                </div>
                                <div className="columns">
                                    <Field.Text name="name" label={t`Company name`} required maxLength={100} />
                                    <div className="field">
                                        <label
                                            className="flex flex-col font-bold text-xs leading-loose pb-4 min-h-[theme(spacing.18)]"
                                            data-taco="label">
                                            {`${t`Company type`}*`}
                                            <div className="relative inline-flex">
                                                <Select2
                                                    name="companyType"
                                                    value={companyType}
                                                    emptyValue={null}
                                                    className="w-64"
                                                    onChange={(newValue: any) => {
                                                        const showError = !newValue || newValue.length === 0;
                                                        setShowCompanyTypeError(showError);
                                                        form.change('companyType', newValue);
                                                        setCompanyType(newValue);
                                                    }}
                                                    multiple
                                                    required>
                                                    {companyTypes.map(company => (
                                                        <Select2.Option key={company.code} value={company.code}>
                                                            {company.type}
                                                        </Select2.Option>
                                                    ))}
                                                </Select2>
                                            </div>
                                            {showCompanyTypeError && (
                                                <span
                                                    className="h-4 text-xs text-left leading-normal font-normal truncate -mb-4 text-red-500"
                                                    role="alert">
                                                    Required
                                                </span>
                                            )}
                                        </label>
                                    </div>
                                    <Field.Text name="email" label={t`E-mail`} required maxLength={50} />
                                </div>
                                <div className="columns">
                                    <Field.Text name="address" label={t`Address`} required maxLength={200} />
                                    <Field.Text name="phone" label={t`Phone no.`} required maxLength={20} />
                                </div>
                                <div className="columns">
                                    <div className="columns">
                                        <Field.Text name="zipCode" label={t`Post code`} required maxLength={10} />
                                        <Field.Text name="city" label={t`City`} required maxLength={50} />
                                    </div>
                                    <Field.Text name="website" label={t`Website`} maxLength={80} />
                                </div>
                            </div>
                            <div className="onboarding__navigation-buttons">
                                <Button appearance="primary" type="submit">
                                    <Trans>Next</Trans>
                                </Button>
                            </div>
                        </div>
                    )}
                </Form>
            </div>
        </div>
    );
}
